import React from "react";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { Dropdown, FormControl, FormGroup } from "react-bootstrap";
import { FiBriefcase, FiClock, FiMoreHorizontal, FiSearch, FiUsers } from "react-icons/fi";
import { toastr } from "react-redux-toastr";

import Avatar from "components/Avatar";
import DataTable from "components/DataTable";
import EmployeeEditDrawer from "components/Employees/EmployeeEditDrawer";
import Loading from "components/Loading";
import { hideModal, showModal } from "actions/modalActions";
import { employeesService } from "services";
import { isHaddockEnvironment } from "utils/documentUtils";
import { errorFormating, formatNumber } from "utils/utils";

const PAGE_SIZE = 40;
const SCROLL_TARGET_ID = "pending-employees-scroll-target";

function compareSortValues(a, b) {
	if (a == null && b == null) return 0;
	if (a == null) return 1;
	if (b == null) return -1;
	if (typeof a === "number" && typeof b === "number") return a - b;
	return String(a).localeCompare(String(b), "es", {
		numeric: true,
		sensitivity: "base",
	});
}

function pendingSortValue(key, employee) {
	switch (key) {
		case "name":
			return [employee.name, employee.surname].filter(Boolean).join(" ") || null;
		case "group":
			return employee.employee_with_group?.group?.name || null;
		case "contract":
			return employee.employee_with_contract?.contract?.name || null;
		case "shiftPattern":
			return employee.employee_with_shift_pattern?.shift_pattern?.name || null;
		default:
			return null;
	}
}

function sortPendingRows(rows, sort) {
	if (!sort || sort.key === "name") return rows;
	const direction = sort.direction === "desc" ? -1 : 1;
	return [...rows].sort((a, b) => {
		const va = pendingSortValue(sort.key, a);
		const vb = pendingSortValue(sort.key, b);
		// El vacío también cambia de extremo al invertir la columna.
		return direction * compareSortValues(va, vb);
	});
}

class PendingEmployeeConfigurations extends React.Component {
	state = {
		rows: [],
		page: 0,
		hasMore: true,
		loading: true,
		loadingMore: false,
		error: null,
		counts: { group: null, contract: null, shiftPattern: null },
		activeFilter: "all",
		query: "",
		groupId: "",
		drawerOpen: false,
		drawerEmployee: null,
		drawerTab: "info",
		drawerAssignmentTab: null,
		drawerRequestId: 0,
		sort: null,
	};

	componentDidMount() {
		this.loadCounts();
		this.loadRows(false);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.refreshVersion !== this.props.refreshVersion) {
			this.refresh();
		}
	}

	componentWillUnmount() {
		if (this.searchTimer) window.clearTimeout(this.searchTimer);
		if (this.requestController) this.requestController.abort();
	}

	missingParams = (filter = this.state.activeFilter) => {
		const includeShiftPattern = !isHaddockEnvironment();
		return {
			missing_group: filter === "all" || filter === "group",
			missing_contract: filter === "all" || filter === "contract",
			...(includeShiftPattern
				? { missing_shift_pattern: filter === "all" || filter === "shiftPattern" }
				: {}),
		};
	};

	loadCounts = async () => {
		const countRequest = (criterion) =>
			employeesService.getAll({
				enabled: true,
				variant: "select",
				page: 0,
				page_size: 1,
				q: this.state.query,
				group_id: this.state.groupId,
				...this.missingParams(criterion),
			});

		try {
			const requests = [countRequest("group"), countRequest("contract")];
			if (!isHaddockEnvironment()) requests.push(countRequest("shiftPattern"));
			const [group, contract, shiftPattern] = await Promise.all(requests);
			this.setState({
				counts: {
					group: group?.pagination?.total_items ?? 0,
					contract: contract?.pagination?.total_items ?? 0,
					shiftPattern: isHaddockEnvironment()
						? null
						: shiftPattern?.pagination?.total_items ?? 0,
				},
			});
		} catch (_) {
			this.setState({ counts: { group: null, contract: null, shiftPattern: null } });
		}
	};

	loadRows = async (append) => {
		if (append && (!this.state.hasMore || this.state.loadingMore || this.state.loading)) return;
		if (!append && this.requestController) this.requestController.abort();

		const page = append ? this.state.page + 1 : 0;
		const controller = new AbortController();
		if (!append) this.requestController = controller;
		this.setState(append
			? { loadingMore: true }
			: { rows: [], page: 0, hasMore: true, loading: true, error: null });

		try {
			// This view needs the current assignments as well as the missing flags, so it
			// intentionally uses the detailed employee response for each paginated page.
			const response = await employeesService.getAll({
				enabled: true,
				q: this.state.query,
				group_id: this.state.groupId,
				page,
				page_size: PAGE_SIZE,
				sort: this.nameSortQuery(),
				...this.missingParams(),
			}, controller.signal);

			if (!response.ok) throw new Error(response.errors?.[0]?.description || this.props.t("pendingConfigurations.loadError"));
			const newRows = Array.isArray(response.data) ? response.data : [];
			const rows = append ? [...this.state.rows, ...newRows] : newRows;
			const total = response.pagination?.total_items ?? 0;
			this.requestController = null;
			this.setState({
				rows,
				page,
				hasMore: total > 0 ? rows.length < total : newRows.length === PAGE_SIZE,
				loading: false,
				loadingMore: false,
				error: null,
			});
		} catch (error) {
			if (error.name === "AbortError") return;
			this.setState({ loading: false, loadingMore: false, hasMore: false, error: error.message });
		}
	};

	refresh = () => {
		this.loadCounts();
		this.loadRows(false);
	};

	selectFilter = (activeFilter) => {
		if (activeFilter === this.state.activeFilter) activeFilter = "all";
		this.setState({ activeFilter }, () => this.loadRows(false));
	};

	handleQueryChange = (event) => {
		const query = event.target.value;
		this.setState({ query });
		if (this.searchTimer) window.clearTimeout(this.searchTimer);
		if (query.length === 0 || query.length >= 3) {
			this.searchTimer = window.setTimeout(() => {
				this.loadCounts();
				this.loadRows(false);
			}, 400);
		}
	};

	handleGroupChange = (event) => {
		this.setState({ groupId: event.target.value }, () => this.refresh());
	};

	nameSortQuery = () => {
		const { sort } = this.state;
		if (!sort || sort.key !== "name") return undefined;
		return sort.direction === "desc" ? "-name,-surname" : "+name,+surname";
	};

	handleSortChange = (sort) => {
		this.setState({ sort }, () => {
			if (sort.key === "name") this.loadRows(false);
		});
	};

	openDrawer = (employee, tab = "info", assignmentTab = null) => {
		this.setState((prev) => ({
			drawerOpen: true,
			drawerEmployee: employee,
			drawerTab: tab,
			drawerAssignmentTab: assignmentTab,
			drawerRequestId: prev.drawerRequestId + 1,
		}));
	};

	hideFromPending = async (employee) => {
		const response = await employeesService.update({ ...employee, show_as_pending: false });
		if (!response.ok) {
			toastr.error(this.props.t("common.error"), errorFormating(response).general || this.props.t("pendingConfigurations.updateError"));
			return;
		}
		toastr.success(
			this.props.t("common.success"),
			this.props.t("pendingConfigurations.hiddenSuccess", { name: `${employee.name} ${employee.surname}` })
		);
		this.refresh();
	};

	confirmDeactivate = (employee) => {
		this.props.showModal({
			open: true,
			title: this.props.t("pendingConfigurations.deactivate"),
			style: { width: "400px" },
			message: this.props.t("pendingConfigurations.deactivateConfirm", { name: `${employee.name} ${employee.surname}` }),
			deleteAction: async () => {
				const response = await employeesService.update({ ...employee, enabled: false });
				this.props.hideModal();
				if (!response.ok) toastr.error(this.props.t("common.error"), errorFormating(response).id);
				else this.refresh();
			},
			closeModal: this.props.hideModal,
		}, "delete");
	};

	renderMissingLink = (employee, relationship, label, tab) => {
		if (relationship) {
			return relationship.group?.name || relationship.contract?.name || relationship.shift_pattern?.name || "—";
		}
		return (
			<button type="button" className="pending-configurations-assign" onClick={() => this.openDrawer(employee, tab, tab)}>
				+ {label}
			</button>
		);
	};

	getColumns = () => {
		const columns = [
			{
				key: "name",
				header: this.props.t("pendingConfigurations.employee"),
				render: (employee) => (
					<div className="pending-configurations-employee">
						<Avatar userId={employee.id} className="avatar-sm" alt={employee.name} />
						<div><button type="button" className="btn-transparent employee-name" onClick={() => this.openDrawer(employee)}>{employee.name} {employee.surname}</button><span>{employee.email}</span></div>
					</div>
				),
			},
			{
				key: "contract",
				header: this.props.t("pendingConfigurations.contract"),
				render: (employee) => this.renderMissingLink(employee, employee.employee_with_contract, this.props.t("pendingConfigurations.assignContract"), "contratos"),
			},
			{
				key: "group",
				header: this.props.t("pendingConfigurations.group"),
				render: (employee) => this.renderMissingLink(employee, employee.employee_with_group, this.props.t("pendingConfigurations.assignGroup"), "grupos"),
			},
		];
		if (!isHaddockEnvironment()) {
			columns.push({
				key: "shiftPattern",
				header: this.props.t("pendingConfigurations.shiftPattern"),
				render: (employee) => this.renderMissingLink(employee, employee.employee_with_shift_pattern, this.props.t("pendingConfigurations.assignShiftPattern"), "ruedas-turnos"),
			});
		}
		return columns;
	};

	renderActions = (employee) => (
		<Dropdown>
			<Dropdown.Toggle variant="action"><FiMoreHorizontal /></Dropdown.Toggle>
			<Dropdown.Menu>
				<Dropdown.Item as="button" onClick={() => this.openDrawer(employee)}>{this.props.t("employees.viewProfile")}</Dropdown.Item>
				<Dropdown.Item as="button" onClick={() => this.confirmDeactivate(employee)}>{this.props.t("pendingConfigurations.deactivate")}</Dropdown.Item>
				<Dropdown.Item as="button" onClick={() => this.hideFromPending(employee)}>{this.props.t("pendingConfigurations.hide")}</Dropdown.Item>
			</Dropdown.Menu>
		</Dropdown>
	);

	renderCard = (key, title, value, icon) => (
		<button
			type="button"
			className={`pending-configurations-card ${this.state.activeFilter === key ? "is-active" : ""}`}
			onClick={() => this.selectFilter(key)}
			aria-pressed={this.state.activeFilter === key}
		>
			<span className="pending-configurations-card-title">{title}</span>
			<span className="pending-configurations-card-icon">{icon}</span>
			<strong>{value == null ? "—" : formatNumber(value)}</strong>
			<small>{this.props.t("pendingConfigurations.filteredWorkforce")}</small>
		</button>
	);

	render() {
		const { groups = [] } = this.props;
		const { rows, loading, loadingMore, hasMore, error, counts } = this.state;
		return (
			<div className="pending-configurations">
				<div className="pending-configurations-cards">
					{this.renderCard("contract", this.props.t("pendingConfigurations.missingContract"), counts.contract, <FiBriefcase />)}
					{this.renderCard("group", this.props.t("pendingConfigurations.missingGroup"), counts.group, <FiUsers />)}
					{!isHaddockEnvironment() && this.renderCard("shiftPattern", this.props.t("pendingConfigurations.missingShiftPattern"), counts.shiftPattern, <FiClock />)}
				</div>

				<div className="pending-configurations-filters">
					<FormGroup className="form-group form-search" controlId="pendingEmployeeSearch">
						<FormControl type="text" placeholder={this.props.t("employees.searchPlaceholder")} value={this.state.query} onChange={this.handleQueryChange} />
						<FiSearch />
					</FormGroup>
					<FormGroup className="form-group" controlId="pendingGroupFilter">
						<FormControl as="select" value={this.state.groupId} onChange={this.handleGroupChange}>
							<option value="">{this.props.t("employees.filterByGroup")}</option>
							{groups.map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
						</FormControl>
					</FormGroup>
				</div>

				{loading && rows.length === 0 ? <Loading /> : error ? <p className="text-danger">{error}</p> : (
					<DataTable
						columns={this.getColumns()}
						rows={sortPendingRows(rows, this.state.sort)}
						rowKey={(row) => row.id}
						sortState={this.state.sort}
						onSortChange={this.handleSortChange}
						actionsColumn={{ render: this.renderActions, className: "td-actions" }}
						emptyMessage={this.props.t("pendingConfigurations.empty")}
						scrollContainerId={SCROLL_TARGET_ID}
						scrollContainerClassName="pending-configurations-table-scroll"
						tableClassName="table-employees table-hover"
						infiniteScroll={{
							dataLength: rows.length,
							next: () => this.loadRows(true),
							hasMore,
							loader: loadingMore ? <div className="text-center py-2">{this.props.t("employees.loading")}</div> : null,
							scrollableTarget: SCROLL_TARGET_ID,
							scrollThreshold: 0.9,
						}}
					/>
				)}

				<EmployeeEditDrawer
					isOpen={this.state.drawerOpen}
					onClose={(hasDataChanges) => {
						this.setState({ drawerOpen: false, drawerAssignmentTab: null });
						if (hasDataChanges) this.refresh();
					}}
					onDataChanged={this.refresh}
					employee={this.state.drawerEmployee}
					employeeId={this.state.drawerEmployee?.id}
					initialTab={this.state.drawerTab}
					initialAssignmentTab={this.state.drawerAssignmentTab}
					openRequestId={this.state.drawerRequestId}
				/>
			</div>
		);
	}
}

const mapStateToProps = (reducers) => ({ groups: reducers.groupsReducer.groups });
const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => dispatch(showModal({ modalProps, modalType })),
});

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PendingEmployeeConfigurations));
